// Collects Meta ad-attribution signals available on admin.mart-24.com and
// packages them for the backend `meta` field (which feeds the server-side
// Conversions API + attribution/ROAS storage in ecommerce-server).
//
// Cross-subdomain note: the Meta Pixel writes `_fbp`/`_fbc` to the ROOT domain
// `.mart-24.com`, so a click captured on the landing page (mart-24.com) is
// readable here on admin.mart-24.com. UTM params don't live in a cookie by
// default, so the landing page mirrors them into a shared `m24_attr` cookie
// (also on `.mart-24.com`); we read that here, falling back to this page's URL.

export interface MetaSignals {
  fbp?: string;
  fbc?: string;
  fbclid?: string;
  landingUrl?: string;
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
}

const getCookie = (name: string): string | undefined => {
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1");
  const match = document.cookie.match("(?:^|; )" + escaped + "=([^;]*)");
  return match ? decodeURIComponent(match[1]) : undefined;
};

export const buildMetaSignals = (): MetaSignals => {
  const params = new URLSearchParams(window.location.search);
  const pick = (k: string) => params.get(k)?.trim() || undefined;

  // First-party attribution mirror written by the landing page (see mart24-landing).
  let stored: Partial<MetaSignals> & { fbclid?: string } = {};
  try {
    stored = JSON.parse(getCookie("m24_attr") || "{}");
  } catch {
    stored = {};
  }

  return {
    fbp: getCookie("_fbp"),
    fbc: getCookie("_fbc"),
    fbclid: pick("fbclid") || stored.fbclid,
    landingUrl: stored.landingUrl || window.location.href,
    referrer: stored.referrer || document.referrer || undefined,
    utmSource: pick("utm_source") || stored.utmSource,
    utmMedium: pick("utm_medium") || stored.utmMedium,
    utmCampaign: pick("utm_campaign") || stored.utmCampaign,
    utmContent: pick("utm_content") || stored.utmContent,
    utmTerm: pick("utm_term") || stored.utmTerm,
  };
};
